import { parseISO } from 'date-fns';
import { SensorUtil } from '../../utilities/sensor.util';
import { ISensor, IMappedCode, IRawSensorTelemetry } from '../sensor.model';
import { JunctionBoxSlot } from '../junctionbox.model';
import { inject } from '@angular/core';
import { MeasurementSystemSettings } from '../settings.model';
import { Notification } from './object';
import {
  convertUnitToImperial,
  convertUnitToMetric,
  convertValue,
  isValidDate,
} from '../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { MEASUREMENT_TYPE } from '../../enums/global.enum';
import { LP_UI_CONFIG } from '../../core/tokens';

const GLOBAL_CONSTANTS = GLOBAL_CONSTANT;

export class Sensor implements ISensor {
  // Getters using utility functions
  get sensorDisplayLabel(): string {
    return SensorUtil.getSensorDisplayLabel(this);
  }

  get junctionBoxSelection(): JunctionBoxSlot[] {
    return SensorUtil.getJunctionBoxSelection(this);
  }

  get junctionBoxSlotLabel(): string {
    return SensorUtil.getJunctionBoxSlotLabel(this);
  }

  get isLeakageSensor(): boolean {
    return SensorUtil.isLeakageSensor(this);
  }

  get isStreamTrap(): boolean {
    return SensorUtil.isStreamTrap(this);
  }

  get isRotaingEquipment(): boolean {
    return SensorUtil.isRotatingEquipment(this);
  }

  get Critical(): boolean {
    return (
      !!this.telemetry?.find((telemetry: CurrentTelemetry) => telemetry.critical) && !this.Inactive
    );
  }
  get Warning(): boolean {
    return (
      !!this.telemetry?.find((telemetry: CurrentTelemetry) => telemetry.warning) &&
      !this.Critical &&
      !this.Inactive
    );
  }
  get Inactive(): boolean {
    return !!this.telemetry?.find((telemetry: CurrentTelemetry) => telemetry.inactive);
  }
  get NoData(): boolean {
    return !this.telemetry?.find((telemetry: CurrentTelemetry) => isValidDate(telemetry.Timestamp));
  }

  // Properties from ISensor interface
  public sensorID!: number;
  public sensorName!: string;
  public parentID!: number;
  public parentEquipmentType?: string;
  public markerX?: number;
  public markerY?: number;
  public sensorX?: number;
  public sensorY?: number;
  public markerText!: string;
  public installedBy?: string;
  public geoPosition!: string;
  public junctionBoxSlot?: string;
  public sensorDigitID?: string;
  public edgeDigitID?: string;
  public picture?: string;
  public thumbnail?: string;
  public latitude?: number;
  public longitude?: number;
  public lastSync?: string;
  public externalID?: string;
  public sensorTypeID?: number;
  public sensorDigits?: string;
  public edgeDigits?: string;
  public connected!: boolean;
  public sensorStatus!: string;
  public sensorStatusColor!: string;
  public telemetry?: CurrentTelemetry[];
  public isGhostSensor?: boolean;
  public equipmentSensorX?: number;
  public equipmentSensorY?: number;
  public equipmentMarkerText!: string;
  public inletNumber?: string;
  public outletNumber?: string;
  public DAU?: string;
  public gateway?: string;
  public inletGateway?: string;
  public outletGateway?: string;
  public inletMac?: string;
  public outletMac?: string;
  public inletMacModel?: string;
  public outletMacModel?: string;
  public inletColorCode?: string;
  public outletColorCode?: string;
  public sensorCreationDate?: string;

  constructor(obj?: Partial<Sensor>) {
    if (obj) {
      Object.assign(this, obj);
    }
  }
}
export class CurrentTelemetry {
  unit = '';
  title = '';
  measurementTypeID: number = GLOBAL_CONSTANTS.ZERO;
  svg = '';
  valueType = 'numerical';
  notifications?: Notification[] = [];
  recommendation = '';
  workingMessage = '';
  warningMessage = '';
  criticalMessage = '';
  inactiveMessage = '';
  preload = false;
  page = '';
  value: any = null;

  private _warning = false;
  private _critical = false;
  private _inactive = false;

  constructor(public sensor: Sensor) {}

  get CurrentNotification(): Notification | null {
    if (!this.notifications || this.notifications.length === GLOBAL_CONSTANTS.ZERO) return null;
    return Array.from(this.notifications).sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[GLOBAL_CONSTANTS.ZERO];
  }

  get warning(): boolean {
    const notification = this.CurrentNotification;
    if (!notification) return this._warning;
    return (
      (notification.isWarning || this._warning) &&
      !notification.isCritical &&
      !notification.inactive
    );
  }
  set warning(val: boolean) {
    this._warning = val;
  }

  get critical(): boolean {
    const notification = this.CurrentNotification;
    if (!notification) return this._critical;
    return (notification.isCritical || this._critical) && !notification.inactive;
  }
  set critical(val: boolean) {
    this._critical = val;
  }

  get inactive(): boolean {
    const notification = this.CurrentNotification;
    if (!notification) return this._inactive;
    return notification.inactive || this._inactive;
  }
  set inactive(val: boolean) {
    this._inactive = val;
  }

  get Value(): any {
    return this.value?.val;
  }

  get Timestamp(): Date {
    return parseISO(this.value?.timestamp);
  }

  ApplyMeasurementSystem(measurementSystem: MeasurementSystemSettings): void {
    const currentUnit = this.unit;
    const newUnit =
      measurementSystem.abbreviation === 'im'
        ? convertUnitToImperial(currentUnit)
        : convertUnitToMetric(currentUnit);

    const valType = this.valueType;
    let val = this.value;

    if (valType === 'numerical') {
      if (Array.isArray(val)) {
        val = val.map((v: any) => {
          v.val = convertValue(v.val, currentUnit, newUnit);
          v.valStr = v.val + newUnit;
          return v;
        });
      } else {
        const newVal = convertValue(val.val, currentUnit, newUnit);
        val = {
          val: newVal,
          valStr: newVal + newUnit,
          timestamp: val.timestamp,
        };
      }
    } else if (valType === 'minmax') {
      if (Array.isArray(val)) {
        val = val.map((v: any) => {
          v.val = {
            min: convertValue(v.val.min, currentUnit, newUnit),
            max: convertValue(v.val.max, currentUnit, newUnit),
          };
          v.valStr = {
            min: v.val.min + newUnit,
            max: v.val.max + newUnit,
          };
          return v;
        });
      } else {
        val = {
          val: {
            min: convertValue(val.val.min, currentUnit, newUnit),
            max: convertValue(val.val.max, currentUnit, newUnit),
          },
          valStr: {
            min: convertValue(val.val.min, currentUnit, newUnit) + newUnit,
            max: convertValue(val.val.max, currentUnit, newUnit) + newUnit,
          },
          timestamp: val.timestamp,
        };
      }
    }

    this.unit = newUnit;
    this.value = val;
  }
}
export class RawSensorTelemetry {
  public sensorID!: number;
  public sensorMeasurementTypeID!: number;
  public sensorName!: string;
  public junctionBoxSlot!: string;
  public sensorDigitID!: string;
  public edgeDigitID!: string;
  public sensorMeasurementTypeDesignation!: string;
  public value!: number;
  public measuredValue!: string;
  public valueType!: string;
  public unit!: string;
  public title!: string;
  public svg!: string;
  public minMaxSibling!: number;
  public timestamp!: Date;
  public sensorStatus!: string;
  public sensorStatusColor!: string;
  public objectStatusID!: number;
  public objectStatus!: string;
  public objectStatusColor!: string;
  public warning!: boolean;
  public critical!: boolean;
  public inactive!: boolean;
  public recommendation!: string;
  public page!: string;
  public workingMessage!: string;
  public warningMessage!: string;
  public inactiveMessage!: string;
  public criticalMessage!: string;
  public markerX!: number;
  public markerY!: number;
  public markerText!: number;
  public inletNumber!: string;
  public outletNumber!: string;
  public dau!: string;
  public gateway!: string;
  public geoPosition!: string;
  public inletGateway!: string;
  public outletGateway!: string;
  public inletMac!: string;
  public outletMac!: string;
  public inletMacModel!: string;
  public outletMacModel!: string;
  public inletColorCode!: string;
  public outletColorCode!: string;
  public sensorCreationDate!: string;
}

function groupBy<T>(arr: T[], key: string): T[][] {
  return Object.values(
    arr.reduce(function (rv: any, x: any) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {})
  );
}

function ParseSensorHealthFromRawSensorTelemetry(
  telemetry: RawSensorTelemetry[],
  sensor: Sensor
): CurrentTelemetry[] {
  return telemetry
    .filter((item) => item.page === 'sensor_health')
    .map((item) => ParseTelemetry(item, telemetry, sensor))
    .filter((item) => !!item);
}

export function ParseHealthFromRawSensorTelemetry(
  telemetry: RawSensorTelemetry[]
): CurrentTelemetry[] {
  const telemetryGroupedBySensor = groupBy<RawSensorTelemetry>(telemetry, 'sensorID');

  return telemetryGroupedBySensor
    .map((data: RawSensorTelemetry[]) => {
      const sensor = Object.assign(new Sensor(), {
        sensorID: data[GLOBAL_CONSTANTS.ZERO].sensorID,
        sensorName: data[GLOBAL_CONSTANTS.ZERO].sensorName,
        sensorStatus: data[GLOBAL_CONSTANTS.ZERO].sensorStatus,
        sensorStatusColor: data[GLOBAL_CONSTANTS.ZERO].sensorStatusColor,
        telemetry: null,
        health: null,
        isGhostSensor: false,
        geoPosition: data[GLOBAL_CONSTANTS.ZERO].geoPosition,
      });
      return ParseSensorHealthFromRawSensorTelemetry(data, sensor);
    })
    .flat(2)
    .filter(
      (item: CurrentTelemetry, index, array) =>
        index ===
        array.findIndex(
          (_item: CurrentTelemetry) => _item.measurementTypeID === item.measurementTypeID
        )
    );
}

export function ParseSensorsFromRawSensorTelemetry(telemetry: RawSensorTelemetry[]): Sensor[] {
  const telemetryGroupedBySensor = groupBy<RawSensorTelemetry>(telemetry, 'sensorID');

  return telemetryGroupedBySensor.map((data: RawSensorTelemetry[]) => {
    const sensor: any = Object.assign(new Sensor(), {
      sensorID: data[GLOBAL_CONSTANTS.ZERO].sensorID,
      sensorName: data[GLOBAL_CONSTANTS.ZERO].sensorName,
      junctionBoxSlot: data[GLOBAL_CONSTANTS.ZERO].junctionBoxSlot,
      sensorDigitID: data[GLOBAL_CONSTANTS.ZERO].sensorDigitID,
      edgeDigitID: data[GLOBAL_CONSTANTS.ZERO].edgeDigitID,
      sensorStatus: data[GLOBAL_CONSTANTS.ZERO].sensorStatus,
      sensorStatusColor: data[GLOBAL_CONSTANTS.ZERO].sensorStatusColor,
      telemetry: null,
      health: null,
      isGhostSensor: false,
      markerX: data[GLOBAL_CONSTANTS.ZERO].markerX,
      markerY: data[GLOBAL_CONSTANTS.ZERO].markerY,
      markerText: data[GLOBAL_CONSTANTS.ZERO].markerText,
      inletNumber: data[GLOBAL_CONSTANTS.ZERO].inletNumber,
      outletNumber: data[GLOBAL_CONSTANTS.ZERO].outletNumber,
      DAU: data[GLOBAL_CONSTANTS.ZERO].dau,
      gateway: data[GLOBAL_CONSTANTS.ZERO].gateway,
      geoPosition: data[GLOBAL_CONSTANTS.ZERO].geoPosition,
      inletGateway: data[GLOBAL_CONSTANTS.ZERO].inletGateway,
      outletGateway: data[GLOBAL_CONSTANTS.ZERO].outletGateway,
      inletMac: data[GLOBAL_CONSTANTS.ZERO].inletMac,
      outletMac: data[GLOBAL_CONSTANTS.ZERO].outletMac,
      inletMacModel: data[GLOBAL_CONSTANTS.ZERO].inletMacModel,
      outletMacModel: data[GLOBAL_CONSTANTS.ZERO].outletMacModel,
      inletColorCode: data[GLOBAL_CONSTANTS.ZERO].inletColorCode,
      outletColorCode: data[GLOBAL_CONSTANTS.ZERO].outletColorCode,
      sensorCreationDate: data[GLOBAL_CONSTANTS.ZERO].sensorCreationDate,
    });
    sensor.telemetry = ParseSensorTelemetryFromRawSensorTelemetry(data, sensor).filter(
      (item: any) => item.measurementTypeID !== MEASUREMENT_TYPE.ID_1020
    );
    return sensor;
  });
}

export function ParseSteamTrapFromRawSensorTelemetry(
  telemetry: RawSensorTelemetry[]
): CurrentTelemetry[] {
  const telemetryGroupedBySensor = groupBy<RawSensorTelemetry>(telemetry, 'sensorID');
  const output = telemetryGroupedBySensor
    .map((data: RawSensorTelemetry[]) => {
      const sensor = Object.assign(new Sensor(), {
        sensorID: data[GLOBAL_CONSTANTS.ZERO].sensorID,
        sensorName: data[GLOBAL_CONSTANTS.ZERO].sensorName,
        sensorStatus: data[GLOBAL_CONSTANTS.ZERO].sensorStatus,
        sensorStatusColor: data[GLOBAL_CONSTANTS.ZERO].sensorStatusColor,
        telemetry: null,
        health: null,
        isGhostSensor: false,
        geoPosition: data[GLOBAL_CONSTANTS.ZERO].geoPosition,
      });

      return data
        .filter(
          (item) =>
            item.page === 'steam_trap' ||
            item.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1079 ||
            item.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1080
        )
        .map((item) => ParseTelemetry(item, telemetry, sensor, true))
        .filter((item) => !!item);
    })
    .flat(2);

  const status = output.find(
    (item: any) => item.measurementTypeID === GLOBAL_CONSTANTS.STEAM_TRAP_STATUS_ID
  );
  if (status) {
    output.map((item: any) => {
      if (item.measurementTypeID !== GLOBAL_CONSTANTS.STEAM_TRAP_STATUS_ID) {
        item.critical = status.critical;
        item.warning = status.warning;
      }
    });
  }
  return output;
}

export class TypedSensorTelemetry<T, TStr> extends CurrentTelemetry {
  override value: { val: T; valStr: TStr; timestamp: Date } = {
    val: null as any,
    valStr: null as any,
    timestamp: new Date(),
  };
}

function ParseTelemetry(
  telemetry: RawSensorTelemetry,
  telemetryList: RawSensorTelemetry[],
  sensor: Sensor,
  preload = false
): CurrentTelemetry | null {
  const metadata = {
    unit: telemetry.unit,
    title: telemetry.title,
    measurementTypeID: telemetry.sensorMeasurementTypeID,
    svg: telemetry.svg,
    warning: telemetry.warning,
    critical: telemetry.critical,
    inactive: telemetry.inactive,
    recommendation: telemetry.recommendation,
    workingMessage: telemetry.workingMessage,
    warningMessage: telemetry.warningMessage,
    inactiveMessage: telemetry.inactiveMessage,
    criticalMessage: telemetry.criticalMessage,
    valueType: telemetry.valueType,
    page: telemetry.page,
    preload: preload,
  };

  metadata.inactive = !!telemetryList.find(
    (telemetry) =>
      (telemetry.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1078 ||
        telemetry.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1079 ||
        telemetry.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1080 ||
        telemetry.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1082) &&
      telemetry.critical
  );

  if (telemetry.minMaxSibling !== null) {
    const sibling = telemetryList.find(
      (item) => telemetry.minMaxSibling === item.sensorMeasurementTypeID
    );
    if (sibling) {
      let siblingIsMin = sibling.value < telemetry.value;
      if (sibling.value === telemetry.value)
        siblingIsMin = sibling.sensorMeasurementTypeID < telemetry.sensorMeasurementTypeID;
      if (
        siblingIsMin &&
        telemetry.sensorMeasurementTypeID !== MEASUREMENT_TYPE.ID_1026 &&
        telemetry.sensorMeasurementTypeID !== MEASUREMENT_TYPE.ID_1073
      )
        return null;

      const _value = {
        val: {
          min: telemetry.value,
          max: sibling.value,
        },
        valStr: {
          min: telemetry.measuredValue,
          max: sibling.measuredValue,
        },
        timestamp: telemetry.timestamp,
      };
      return Object.assign(
        new TypedSensorTelemetry<{ min: number; max: number }, { min: string; max: string }>(
          sensor
        ),
        Object.assign({}, metadata, {
          valueType: 'minmax',
          value: _value,
        })
      );
    }
  }

  // numerical telemetry

  const value = {
    val: telemetry.value,
    valStr: telemetry.measuredValue,
    timestamp: telemetry.timestamp,
  };

  return Object.assign(new TypedSensorTelemetry<number, string>(sensor), metadata, {
    valueType: metadata.valueType ? metadata.valueType : 'numerical',
    value: value,
  });
}

function ParseSensorTelemetryFromRawSensorTelemetry(
  telemetry: RawSensorTelemetry[],
  sensor: Sensor
): CurrentTelemetry[] {
  return telemetry
    .filter(
      (item) =>
        (!!item.page && item.page !== 'sensor_health') ||
        item.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1078 ||
        item.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1079 ||
        item.sensorMeasurementTypeID === MEASUREMENT_TYPE.ID_1080
    )
    .map((item) => ParseTelemetry(item, telemetry, sensor))
    .filter((item) => !!item);
}

export function ParseTemperatureFromRawSensorTelemetry(
  telemetry: RawSensorTelemetry[]
): CurrentTelemetry | undefined {
  const telemetryGroupedBySensor = groupBy<RawSensorTelemetry>(telemetry, 'sensorID');

  return telemetryGroupedBySensor
    .map((data: RawSensorTelemetry[]) => {
      const sensor = Object.assign(new Sensor(), {
        sensorID: data[GLOBAL_CONSTANTS.ZERO].sensorID,
        sensorName: data[GLOBAL_CONSTANTS.ZERO].sensorName,
        sensorStatus: data[GLOBAL_CONSTANTS.ZERO].sensorStatus,
        sensorStatusColor: data[GLOBAL_CONSTANTS.ZERO].sensorStatusColor,
        telemetry: null,
        health: null,
        isGhostSensor: false,
        geoPosition: data[GLOBAL_CONSTANTS.ZERO].geoPosition,
      });
      return ParseSensorTelemetryFromRawSensorTelemetry(data, sensor);
    })
    .flat(2)
    .find((item: CurrentTelemetry) => item.measurementTypeID === MEASUREMENT_TYPE.ID_1020);
}

export class SerialNumber implements IMappedCode {
  private readonly cfg = inject(LP_UI_CONFIG);
  public digits!: string;
  public device!: string;
  public scanned!: boolean;
  public uploaded!: boolean;
  public mappingValid!: boolean;
  public modelNumber!: string;
  public modelNumberValid!: boolean;
  public loading!: boolean;

  constructor(device: string) {
    this.device = device;
  }

  get value(): string | null {
    return this.empty ? null : this.digits.toString();
  }

  get empty(): boolean {
    return !this.digits || this.digits.length === 0;
  }

  get validCode(): boolean {
    return (
      (!this.cfg.enforceIdMapping || (this.mappingValid && this.modelNumberValid)) &&
      this.digits.length === 10 &&
      this.firstTwoCharsAlphabets &&
      this.alphanumeric
    );
  }

  get onlyDigits(): boolean {
    return !!(this.digits && !isNaN(+this.digits) && +this.digits >= 0);
  }

  get alphanumeric(): boolean {
    if (this.digits && this.digits != '') {
      return this.digits.match(/^[a-z0-9]+$/i) ? true : false;
    }
    return false;
  }

  get firstTwoCharsAlphabets(): boolean {
    if (this.digits && this.digits != '') {
      return this.digits.charAt(0).match(/^[a-z]+$/i) && this.digits.charAt(1).match(/^[a-z]+$/i)
        ? true
        : false;
    }
    return false;
  }

  get validLength(): boolean {
    return ('' + this.digits).length === 10;
  }
}

export class QrCode implements IMappedCode {
  private readonly cfg = inject(LP_UI_CONFIG);
  public digits!: number;
  public device!: string;
  public scanned!: boolean;
  public uploaded!: boolean;
  public mappingValid!: boolean;
  public loading!: boolean;

  constructor(device: string) {
    this.device = device;
  }

  get value(): string | null {
    return this.empty ? null : this.digits.toString();
  }

  get empty(): boolean {
    return !this.digits || this.digits === 0;
  }

  get validCode(): boolean {
    return (
      (!this.cfg.enforceIdMapping || this.mappingValid) &&
      this.digits > 9999999999 &&
      this.digits < 100000000000
    );
  }

  get onlyDigits(): boolean {
    return !!(this.digits && !isNaN(+this.digits) && +this.digits >= 0);
  }

  get validLength(): boolean {
    return ('' + this.digits).length === 11;
  }
}

export class MacAddress implements IMappedCode {
  private readonly cfg = inject(LP_UI_CONFIG);
  public inputValue!: string;
  public value!: string;
  public modelNumber!: string;
  public device!: string;
  public scanned!: boolean;
  public uploaded!: boolean;
  public mappingValid!: boolean;
  public loading!: boolean;

  // all scanned information. Will not be displayed in this app but saved with sensor
  public dauQrString!: string;

  private punctuation = /:/gi;
  private alphanumerical = /^[a-z0-9]+$/i;
  private hexadecimal = /^[0-9A-F]+$/i;

  constructor(device: string) {
    this.device = device;
  }

  get empty(): boolean {
    return !this.value;
  }

  get validCode(): boolean {
    return (
      (!this.cfg.enforceIdMapping || this.mappingValid) && this.alphanumerical.test(this.value)
    );
  }

  get onlyAlphanumerical(): boolean {
    return !!(this.value && this.alphanumerical.test(this.value));
  }

  get onlyHexadecimal(): boolean {
    return !!(this.value && this.hexadecimal.test(this.value));
  }

  get onlyPairs(): boolean {
    return !!(this.value && this.value.length % 2 === 0);
  }

  get validLength(): boolean {
    return !!this.value && this.value.length === 12;
  }

  public updateFromUploadOrScan(qrString: string, scanned: boolean) {
    //02,S/N:000290,MAC:EB:8F:63:6E:0E:9F,MODEL:A-DU-600-0000_0
    this.dauQrString = qrString;

    const macAddressString =
      this.dauQrString.split(',').length > 2 ? this.dauQrString.split(',')[2] : '';
    this.inputValue = macAddressString.length > 4 ? macAddressString.slice(4) : '';
    this.value = this.inputValue.replace(this.punctuation, '');
    this.scanned = scanned;
    this.uploaded = !scanned;
  }
}

export class MatrixCode implements IMappedCode {
  private readonly cfg = inject(LP_UI_CONFIG);
  public inputValue!: string;
  public value!: string;
  public device!: string;
  public scanned!: boolean;
  public uploaded!: boolean;
  public mappingValid!: boolean;
  public gatewayDeviceType!: string;
  public reservedSlot = -1;
  public loading!: boolean;

  // all scanned information. Will not be displayed in this app but saved with sensor
  public gatewayQrString!: string;

  private punctuation = /:/gi;
  private alphanumerical = /^[a-z0-9]+$/i;

  constructor(device: string) {
    this.device = device;
  }

  get empty(): boolean {
    return !this.value;
  }

  // gateway is of type Microtronics
  get getIsMt(): boolean {
    return (
      this.microTronicsValidLength && this.alphanumerical.test(this.value) && this.mtStartValueCheck
    );
  }

  // valid code but gateway slot is not validated against database
  get validCode(): boolean {
    return (
      (!this.cfg.enforceIdMapping || this.mappingValid) &&
      this.alphanumerical.test(this.value) &&
      this.validLength
    );
  }

  get onlyAlphanumerical(): boolean {
    return !!(this.value && this.alphanumerical.test(this.value));
  }

  get validLength(): boolean {
    return !!this.value && this.value.length > 3;
  }

  get htstValidLength(): boolean {
    return !!this.value && this.value.length > 9;
  }

  get microTronicsValidLength(): boolean {
    return !!this.value && this.value.length === 16;
  }

  get mtStartValueCheck(): boolean {
    return this.value.startsWith('04');
  }

  get validSlot(): boolean {
    return this.reservedSlot >= 0;
  }

  public updateFromUploadOrScan(qrString: string, scanned: boolean) {
    //10,MODEL:A-GW-0004,S/N:TBCDB1076304
    this.gatewayQrString = qrString;
    const gatewayString =
      this.gatewayQrString.split(',').length > 2 ? this.gatewayQrString.split(',')[2] : '';
    this.inputValue = gatewayString.length > 4 ? gatewayString.slice(4) : '';
    this.value = this.inputValue.replace(this.punctuation, '');
    this.scanned = scanned;
    this.uploaded = !scanned;
  }
}

function ParseTelemetryIA(
  telemetry: IRawSensorTelemetry,
  telemetryList: IRawSensorTelemetry[],
  sensor: Sensor,
  preload = false
): CurrentTelemetry | null {
  const metadata = {
    unit: telemetry.unit,
    title: telemetry.title,
    measurementTypeID: telemetry.sensorMeasurementTypeID,
    svg: telemetry.svg,
    warning: telemetry.warning,
    critical: telemetry.critical,
    recommendation: telemetry.recommendation,
    workingMessage: telemetry.workingMessage,
    warningMessage: telemetry.warningMessage,
    criticalMessage: telemetry.criticalMessage,
    valueType: telemetry.valueType,
    page: telemetry.page,
    preload: preload,
  };
  // min-max telemetry
  if (telemetry.minMaxSibling !== null) {
    const sibling = telemetryList.find(
      (item) => telemetry.minMaxSibling === item.sensorMeasurementTypeID
    );
    if (sibling) {
      let siblingIsMin = sibling.value < telemetry.value;
      if (sibling.value === telemetry.value)
        siblingIsMin = sibling.sensorMeasurementTypeID < telemetry.sensorMeasurementTypeID;
      // avoid parsing min-max-values twice:
      if (siblingIsMin) return null;

      const _value = {
        val: {
          min: telemetry.value,
          max: sibling.value,
        },
        valStr: {
          min: telemetry.measuredValue,
          max: sibling.measuredValue,
        },
        timestamp: telemetry.timestamp,
      };
      return Object.assign(
        new TypedSensorTelemetry<{ min: number; max: number }, { min: string; max: string }>(
          sensor
        ),
        {
          ...metadata,
          valueType: 'minmax',
          value: _value,
        }
      );
    }
  }
  // numerical telemetry

  const value = {
    val: telemetry.value,
    valStr: telemetry.measuredValue,
    timestamp: telemetry.timestamp,
  };

  return Object.assign(new TypedSensorTelemetry<number, string>(sensor), {
    ...metadata,
    valueType: metadata.valueType ? metadata.valueType : 'numerical',
    value: value,
  });

  // boolean telemetry
  // TODO
}

export function ParseSensorHealthFromRawSensorTelemetryIA(
  telemetry: IRawSensorTelemetry[],
  sensor: Sensor
): CurrentTelemetry[] {
  const x = telemetry.filter((item) => item.page === 'sensor_health');
  const y = x.map((item) => ParseTelemetryIA(item, telemetry, sensor));

  const z = y.filter((item) => !!item); // necessary because ParseTelemetry can return null in case of min-max-values
  return z;
}

export function ParseHealthFromRawSensorTelemetryIA(
  telemetry: IRawSensorTelemetry[]
): CurrentTelemetry[] {
  const telemetryGroupedBySensor = groupBy<IRawSensorTelemetry>(telemetry, 'sensorID');

  const x = telemetryGroupedBySensor.map((data: IRawSensorTelemetry[]) => {
    const sensor = Object.assign(new Sensor(), {
      sensorID: data[0].sensorID,
      sensorName: data[0].sensorName,
      sensorStatus: data[0].sensorStatus,
      sensorStatusColor: data[0].sensorStatusColor,
      telemetry: null,
      health: null,
      isGhostSensor: false, // TODO
    });
    const x = ParseSensorHealthFromRawSensorTelemetryIA(data, sensor);
    return x;
  });
  const y = x.flat(2);
  const z = y.filter(
    (item: CurrentTelemetry, index, array) =>
      index ===
      array.findIndex(
        (_item: CurrentTelemetry) => _item.measurementTypeID === item.measurementTypeID
      )
  ); // filter out duplicate data (we only want one telemetry object per measurement type)
  return z;
}

export type {
  IUltrasonicReadings,
  INanoPreciseToken,
  INanoprecisePlantAssetDetailList,
  IHtstSensorDetails,
  ISensorHtst,
} from '../sensor.model';
